<template>
  <section
    class="rounded-xl border border-gray-200 bg-white p-4"
    aria-labelledby="question-rate-title">
    <div class="mb-3">
      <h4
        id="question-rate-title"
        class="text-sm font-black text-gray-900">
        問題別正答率
      </h4>
      <p class="mt-1 text-xs font-medium text-gray-500">
        {{ description }}
      </p>
    </div>

    <label class="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-gray-700">
      <span class="font-bold">問題種別</span>
      <select
        v-model.number="selectedExaminationType"
        class="cursor-pointer rounded-md border-2 border-green-600 bg-white py-2 pl-3 pr-10 text-sm font-bold shadow-sm">
        <option
          v-for="(label, type) in examinationTypes"
          :key="type"
          :value="Number(type)">
          {{ label }}
        </option>
      </select>
    </label>

    <div class="rounded-lg border border-gray-100">
      <DataTable
        :key="selectedExaminationType"
        class="question-rate-table max-h-96"
        header-padding-class="px-1"
        hover-disabled
        drag-disabled
        :headers="headers"
        :column-classes="compactColumnClasses"
        :items="filteredItems"
        no-data-text="問題データがありません。">
        <template #index="{ item }">
          <div class="text-center">{{ item.sort_index + 1 }}</div>
        </template>
        <template #personal_result="{ item }">
          <div class="flex justify-center">
            <svg
              v-if="item.personal_result === 'correct'"
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6 text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-label="正解">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <svg
              v-else-if="item.personal_result === 'incorrect'"
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6 text-blue-700"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-label="不正解">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span
              v-else
              class="font-semibold text-gray-500">-</span>
          </div>
        </template>
        <template #school_answer_rate="{ item }">
          <div class="text-center">{{ item.correct_answer_rate }}%</div>
        </template>
        <template #answer_rate="{ item }">
          <div class="text-center">{{ nationalRateLabel(item) }}</div>
        </template>
        <template #question="{ item }">
          <div class="text-left">{{ item.question || '-' }}</div>
        </template>
        <template #subject="{ item }">
          <div class="text-left">{{ subjectName(item.subject_id) }}</div>
        </template>
      </DataTable>
    </div>
  </section>
</template>

<script setup>
import { computed, ref } from 'vue'
import DataTable from '@/Common/DataTable'

const props = defineProps({
  mode: {
    type: String,
    default: 'school',
  },
  items: {
    type: Array,
    default: () => [],
  },
  examinationTypes: {
    type: Object,
    default: () => ({}),
  },
  subjectNames: {
    type: Array,
    default: () => [],
  },
  nationalQuestionRatesReady: {
    type: Boolean,
    default: false,
  },
})

const isStudent = computed(() => props.mode === 'student')
const selectedExaminationType = ref(1)
const filteredItems = computed(() => props.items.filter(
  item => Number(item.examination_type) === Number(selectedExaminationType.value),
))

const compactColumnClasses = {
  index: 'question-rate-number w-px pl-3 pr-1',
  personal_result: 'question-rate-result w-px px-1',
  school_answer_rate: 'question-rate-result w-px px-1',
  answer_rate: 'question-rate-national w-px px-1',
  question: 'px-1',
  subject: 'question-rate-subject px-1',
}

const headers = computed(() => {
  const tableHeaders = {
    index: '問題番号',
  }

  if (isStudent.value) {
    tableHeaders.personal_result = '解答結果'
  } else {
    tableHeaders.school_answer_rate = '校内正答率'
  }

  if (props.nationalQuestionRatesReady) {
    tableHeaders.answer_rate = '全国正答率'
  }

  tableHeaders.question = '問題文'
  tableHeaders.subject = '科目'

  return tableHeaders
})

const description = computed(() => {
  if (isStudent.value) {
    return props.nationalQuestionRatesReady
      ? 'あなたの結果と全国正答率を問題単位で確認できます。'
      : 'あなたの結果を問題単位で確認できます。'
  }

  return props.nationalQuestionRatesReady
    ? '校内正答率と全国正答率を問題単位で確認できます。'
    : '校内正答率を問題単位で確認できます。'
})

const subjectName = (subjectId) => props.subjectNames.find(item => item.id === subjectId)?.name || '-'
const nationalRate = item => item.national_correct_answer_rate ?? item.nationalRate ?? '-'
const nationalRateLabel = (item) => {
  const rate = nationalRate(item)

  return rate === '-' ? '-' : `${rate}%`
}

</script>

<style scoped>
@media (max-width: 770px) {
  .question-rate-table :deep(table) {
    table-layout: fixed;
  }

  .question-rate-table :deep(th),
  .question-rate-table :deep(td) {
    white-space: normal;
    overflow-wrap: anywhere;
  }

  .question-rate-table :deep(.question-rate-number) {
    width: 16%;
  }

  .question-rate-table :deep(.question-rate-result) {
    width: 10%;
  }

  .question-rate-table :deep(.question-rate-national) {
    width: 16%;
  }

  .question-rate-table :deep(.question-rate-subject) {
    width: 18%;
  }
}
</style>
